// src/features/vue-role/contexte.tsx — SCE Issues
// Bascule de rôle de l'administrateur (Lot 7, demande 13) : l'état et ses gardes.
//
// ─── Ce que fait la bascule ───
//
// L'administrateur voit l'application EXACTEMENT comme un coordinateur réel ou
// un opérateur : `useCurrentUser` renvoie le profil choisi à la place du compte
// MSAL, et tout le reste suit (rôle, droits, « Mes déclarations », filtres du
// tableau de bord…).
//
// Elle ne change QUE l'affichage. Les requêtes partent avec le jeton MSAL
// réel : toute écriture reste signée du vrai compte dans la colonne système
// `Author`. D'où trois garde-fous, tous ici ou branchés sur ce module :
//  - un bandeau permanent qui le rappelle (BandeauVueRole) ;
//  - une confirmation avant chaque écriture d'état (useConfirmerEcriture) ;
//  - aucune écriture dans les préférences de la personne affichée
//    (useDashboardPrefs) ni dans sa présence (PresenceProvider lit le compte réel).
//
// ─── Pourquoi ce fichier est petit, et le reste chargé à la demande ───
//
// `estAdmin()` est une valeur de RUNTIME : aucun bundler ne peut retirer du
// code sur cette base (cadrage, décision 12). Ce module-ci est donc livré à
// tous — quelques centaines d'octets : un contexte, un état, des gardes. Le
// sélecteur, les profils et le bandeau sont en import dynamique
// (`React.lazy`) : un non-administrateur ne les télécharge jamais.
//
// ─── Le piège de la récursion d'identité ───
//
// Le droit d'utiliser la bascule se décide sur le compte MSAL RÉEL, lu ici via
// `useAuth` — jamais via `useCurrentUser`, qui renvoie le profil affiché. Sinon
// l'administrateur passé en vue Opérateur cesserait d'être administrateur, et
// ne pourrait plus revenir.
//
// Persistance : sessionStorage — fermer l'onglet ramène à sa propre vue.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useAuth } from '@/auth/useAuth'
import { estAdmin } from '@/config/admin.config'
import { isCoordinateur } from '@/config/coordinateurs.config'
import type { UserRole } from '@/types/issue.types'
import { nomComplet } from '@/utils/noms'

/** Une identité d'affichage proposée par le sélecteur. */
export interface ProfilVue {
  /** Clé stable du sélecteur. */
  cle: string
  /** Libellé du sélecteur et du bandeau (« Thibaut Hacquin »). */
  libelle: string
  /** Précision (« Coordinateur ELEC + AUTOM »). */
  precision: string
  upn: string
  nom: string
  email: string
  role: UserRole
}

interface ValeurVueRole {
  /** Profil affiché, ou null : l'administrateur voit sa propre vue. */
  profil: ProfilVue | null
  /** Le compte MSAL RÉEL a-t-il droit à la bascule ? */
  autorise: boolean
  /** Nom du compte réel — celui qui signe les écritures. */
  nomReel: string
  choisir: (p: ProfilVue) => void
  revenir: () => void
}

const INERTE: ValeurVueRole = {
  profil: null,
  autorise: false,
  nomReel: '',
  choisir: () => undefined,
  revenir: () => undefined,
}

const Contexte = createContext<ValeurVueRole>(INERTE)

const CLE_STOCKAGE = 'sce-issues.vue-role'
const ROLES: readonly UserRole[] = ['operateur', 'coordinateur', 'fournisseur']

/**
 * Relit le profil mémorisé pour l'onglet. Le profil est stocké en entier (pas
 * seulement sa clé) pour que ce module n'ait pas à embarquer la liste des
 * profils. Il est donc revérifié : un coordinateur doit l'être encore dans la
 * config, sinon on revient à sa propre vue plutôt que d'afficher un rôle faux.
 */
function lireProfilMemorise(): ProfilVue | null {
  try {
    const brut = sessionStorage.getItem(CLE_STOCKAGE)
    if (!brut) return null
    const p = JSON.parse(brut) as Partial<ProfilVue>
    if (
      typeof p.cle !== 'string' || typeof p.upn !== 'string' ||
      typeof p.nom !== 'string' || typeof p.email !== 'string' ||
      typeof p.libelle !== 'string' || typeof p.precision !== 'string' ||
      !ROLES.includes(p.role as UserRole)
    ) {
      return null
    }
    if (p.role === 'coordinateur' && !isCoordinateur(p.upn)) return null
    return p as ProfilVue
  } catch {
    // sessionStorage indisponible (navigation privée, iframe…) : vue normale.
    return null
  }
}

export function VueRoleProvider({ children }: { children: ReactNode }): JSX.Element {
  const { account } = useAuth()
  const upnReel = account?.username ?? ''
  // En développement local, la bascule reste ouverte à tous (outil de test).
  const autorise = import.meta.env.DEV || estAdmin(upnReel)

  const [profilChoisi, setProfilChoisi] = useState<ProfilVue | null>(lireProfilMemorise)

  useEffect(() => {
    try {
      if (profilChoisi) sessionStorage.setItem(CLE_STOCKAGE, JSON.stringify(profilChoisi))
      else sessionStorage.removeItem(CLE_STOCKAGE)
    } catch {
      // Idem : pas de persistance, pas d'erreur.
    }
  }, [profilChoisi])

  const choisir = useCallback((p: ProfilVue) => setProfilChoisi(p), [])
  const revenir = useCallback(() => setProfilChoisi(null), [])

  const valeur = useMemo<ValeurVueRole>(
    () => ({
      // Défense en profondeur : sans droit, aucun profil n'est appliqué,
      // même s'il traîne dans sessionStorage (compte partagé sur un poste).
      profil: autorise ? profilChoisi : null,
      autorise,
      nomReel: nomComplet(account?.name ?? upnReel, upnReel),
      choisir,
      revenir,
    }),
    [autorise, profilChoisi, account?.name, upnReel, choisir, revenir],
  )

  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>
}

/** État de la bascule. Sans Provider monté : état inerte, vue normale. */
export function useVueRole(): ValeurVueRole {
  return useContext(Contexte)
}

/**
 * Confirmation avant une écriture d'état (qualifier, annuler, clore,
 * modifier, déclarer…) pendant la bascule. Hors bascule : toujours vrai, sans
 * rien demander.
 *
 * Volontairement PAS sur les commentaires : la friction ferait désactiver le
 * garde-fou au bout d'une journée (cadrage, décision 12).
 *
 * @returns une fonction `(action) => boolean` — false si l'administrateur annule.
 */
export function useConfirmerEcriture(): (action: string) => boolean {
  const { profil, nomReel } = useVueRole()
  return useCallback(
    (action: string): boolean => {
      if (!profil) return true
      // eslint-disable-next-line no-alert
      return window.confirm(
        `Vue de ${profil.nom} (${profil.precision})\n\n` +
        `« ${action} » sera enregistré sous VOTRE compte : ${nomReel}.\n` +
        'L\'historique SharePoint (colonne « Créé par ») portera votre nom, pas le sien.\n\n' +
        'Continuer ?',
      )
    },
    [profil, nomReel],
  )
}
